import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import { VariableResolver } from './variableResolver';

type ThemeProperty = { value: string } | Record<string, unknown>;
type ThemePropertiesMap = Record<string, ThemeProperty>;

export class ThemesProvider {
  private _themeVariables: Record<string, string> = {};

  constructor(private componentsAPI: ComponentsPlatformService) {
    this._themeVariables = this.getThemeVariables();
  }

  get themeVariables() {
    return this._themeVariables;
  }

  /**
   * Fetches and resolves all theme variables from the components API.
   * Handles nested variable references (e.g., var(--x) pointing to var(--y)).
   */
  private getThemeVariables(): Record<string, string> {
    try {
      const themePropertiesMap =
        this.componentsAPI.theme.getThemeCustomProperties();

      return this.resolveAllThemeProperties(themePropertiesMap);
    } catch (error) {
      console.error(
        '[ThemesProvider] Failed to resolve theme variables:',
        error,
      );
      return {};
    }
  }

  /**
   * Resolves all theme properties to their final values.
   */
  private resolveAllThemeProperties(
    themePropertiesMap: ThemePropertiesMap,
  ): Record<string, string> {
    const resolvedVariables: Record<string, string> = {};

    for (const [propertyName, property] of Object.entries(themePropertiesMap)) {
      const resolvedValue = this.resolvePropertyValue(
        property,
        themePropertiesMap,
      );

      if (resolvedValue !== null) {
        resolvedVariables[propertyName] = resolvedValue;
      }
    }

    return resolvedVariables;
  }

  /**
   * Resolves a single property value, following variable references recursively.
   * Returns null if the property cannot be resolved.
   */
  private resolvePropertyValue(
    property: ThemeProperty,
    themePropertiesMap: ThemePropertiesMap,
  ): string | null {
    if (!this.hasValue(property)) {
      return null;
    }

    const initialValue = property.value;
    const targetPropertyName = VariableResolver.isSingleCSSVariable(
      initialValue,
    )
      ? VariableResolver.extractVarName(initialValue)
      : null;

    const startingProperty = targetPropertyName
      ? themePropertiesMap[targetPropertyName]
      : property;

    if (!startingProperty || !this.hasValue(startingProperty)) {
      return null;
    }

    return this.resolveNestedReferences(startingProperty, themePropertiesMap);
  }

  /**
   * Follows the chain of variable references until a concrete value is found.
   * Tracks visited variables to prevent infinite loops from circular references.
   */
  private resolveNestedReferences(
    property: ThemeProperty & { value: string },
    themePropertiesMap: ThemePropertiesMap,
  ): string | null {
    let currentProperty: ThemeProperty | undefined = property;
    const visited = new Set<string>();

    while (
      currentProperty &&
      this.hasValue(currentProperty) &&
      VariableResolver.isSingleCSSVariable(currentProperty.value)
    ) {
      const referencedName = VariableResolver.extractVarName(
        currentProperty.value,
      );

      // Prevent infinite loops from circular references
      if (visited.has(referencedName)) {
        return null;
      }
      visited.add(referencedName);

      currentProperty = themePropertiesMap[referencedName];
    }

    if (currentProperty && this.hasValue(currentProperty)) {
      return currentProperty.value;
    }

    return null;
  }

  /**
   * Type guard to check if a property has a value field.
   */
  private hasValue(
    property: ThemeProperty,
  ): property is { value: string } & Record<string, unknown> {
    return 'value' in property;
  }

  /**
   * Builds a map of theme variables for resolution.
   * Filters the theme variables to only include those referenced in the provided properties.
   */
  buildThemeVariablesMap(
    cssPropsRaw: Record<string, string>,
    customPropsRaw: Record<string, string>,
  ): Record<string, string> {
    const allVariableNames = VariableResolver.extractAllVariableNames(
      cssPropsRaw,
      customPropsRaw,
    );

    if (allVariableNames.length === 0) {
      return {};
    }

    const result: Record<string, string> = {};

    for (const variableName of allVariableNames) {
      const resolvedValue = this.themeVariables[variableName];

      if (resolvedValue) {
        result[variableName] = resolvedValue;
      }
    }

    return result;
  }
}
